export const SPEAKER_SAMPLE_RATE = 16000
export const SPEAKER_MAX_SOURCE_BYTES = 10 * 1024 * 1024
export const SPEAKER_MAX_DURATION_MS = 3 * 60 * 1000
export const SPEAKER_MAX_PCM_BYTES = SPEAKER_SAMPLE_RATE * 2 * (SPEAKER_MAX_DURATION_MS / 1000)

export interface SpeakerPcmAudio {
  blob: Blob
  durationMs: number
  fileName: string
}

export class SpeakerAudioError extends Error {
  constructor (message: string) {
    super(message)
    this.name = 'SpeakerAudioError'
  }
}

const AUDIO_FILE_PATTERN = /\.(aac|m4a|mp3|wav)$/i
const ALLOWED_AUDIO_MIME_TYPES = new Set([
  'audio/aac',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
  'audio/x-m4a',
  'audio/x-wav',
])
const RECORDED_AUDIO_MIME_TYPES = new Set(['audio/webm', 'audio/webm;codecs=opus'])

export function validateSpeakerAudioSource (blob: Blob, fileName: string, allowRecordedFormat = false) {
  if (blob.size <= 0) {
    throw new SpeakerAudioError('音频文件为空')
  }
  if (blob.size > SPEAKER_MAX_SOURCE_BYTES) {
    throw new SpeakerAudioError('音频文件不能超过 10 MB')
  }
  const mimeType = String(blob.type || '').toLowerCase()
  const baseMimeType = mimeType.split(';')[0]
  const mimeAllowed = ALLOWED_AUDIO_MIME_TYPES.has(baseMimeType) ||
    (allowRecordedFormat && RECORDED_AUDIO_MIME_TYPES.has(mimeType))
  const extensionFallbackAllowed = (!mimeType || mimeType === 'application/octet-stream') && AUDIO_FILE_PATTERN.test(fileName)
  if (!mimeAllowed && !extensionFallbackAllowed) {
    throw new SpeakerAudioError('仅支持浏览器可解码的 MP3、WAV、M4A 或 AAC 音频')
  }
}

export function downmixSpeakerChannels (channels: Float32Array[]): Float32Array {
  if (channels.length === 0) {
    return new Float32Array(0)
  }
  const length = Math.max(...channels.map(channel => channel.length))
  const mono = new Float32Array(length)
  for (let index = 0; index < length; index += 1) {
    let total = 0
    let count = 0
    channels.forEach(channel => {
      if (index < channel.length) {
        total += channel[index]
        count += 1
      }
    })
    mono[index] = count > 0 ? total / count : 0
  }
  return mono
}

export function resampleSpeakerAudio (
  samples: Float32Array,
  sourceSampleRate: number,
  targetSampleRate = SPEAKER_SAMPLE_RATE
): Float32Array {
  if (!Number.isFinite(sourceSampleRate) || sourceSampleRate <= 0 || !Number.isFinite(targetSampleRate) || targetSampleRate <= 0) {
    throw new SpeakerAudioError('音频采样率无效')
  }
  if (samples.length === 0) {
    return new Float32Array(0)
  }
  if (sourceSampleRate === targetSampleRate) {
    return samples.slice()
  }
  const targetLength = Math.max(1, Math.round(samples.length * targetSampleRate / sourceSampleRate))
  const result = new Float32Array(targetLength)
  const ratio = sourceSampleRate / targetSampleRate
  if (ratio > 1) {
    for (let index = 0; index < targetLength; index += 1) {
      const sourceStart = index * ratio
      const sourceEnd = Math.min(samples.length, (index + 1) * ratio)
      const firstSample = Math.floor(sourceStart)
      const lastSample = Math.min(samples.length - 1, Math.ceil(sourceEnd) - 1)
      let weightedTotal = 0
      let totalWeight = 0
      for (let sourceIndex = firstSample; sourceIndex <= lastSample; sourceIndex += 1) {
        const overlap = Math.max(0, Math.min(sourceEnd, sourceIndex + 1) - Math.max(sourceStart, sourceIndex))
        weightedTotal += samples[sourceIndex] * overlap
        totalWeight += overlap
      }
      result[index] = totalWeight > 0 ? weightedTotal / totalWeight : 0
    }
    return result
  }
  for (let index = 0; index < targetLength; index += 1) {
    const sourcePosition = index * ratio
    const leftIndex = Math.min(samples.length - 1, Math.floor(sourcePosition))
    const rightIndex = Math.min(samples.length - 1, leftIndex + 1)
    const fraction = sourcePosition - leftIndex
    result[index] = samples[leftIndex] + (samples[rightIndex] - samples[leftIndex]) * fraction
  }
  return result
}

export function encodeSpeakerPcm16Le (samples: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(samples.length * 2)
  const view = new DataView(buffer)
  samples.forEach((sample, index) => {
    const clamped = Math.max(-1, Math.min(1, Number.isFinite(sample) ? sample : 0))
    const pcmValue = clamped < 0
      ? Math.round(clamped * 0x8000)
      : Math.round(clamped * 0x7fff)
    view.setInt16(index * 2, pcmValue, true)
  })
  return buffer
}

function pcmFileName (sourceName: string) {
  const baseName = sourceName.replace(/\.[^.]+$/, '').trim() || 'speaker-audio'
  return `${baseName}.pcm`
}

export async function convertSpeakerAudioToPcm (
  source: Blob,
  sourceName: string,
  allowRecordedFormat = false
): Promise<SpeakerPcmAudio> {
  validateSpeakerAudioSource(source, sourceName, allowRecordedFormat)
  const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext
  if (!AudioContextConstructor) {
    throw new SpeakerAudioError('当前浏览器不支持音频解码')
  }
  const context = new AudioContextConstructor()
  try {
    const sourceBuffer = await source.arrayBuffer()
    let decoded: AudioBuffer
    try {
      decoded = await context.decodeAudioData(sourceBuffer.slice(0))
    } catch (_error) {
      throw new SpeakerAudioError('浏览器无法解码该音频，请改用 MP3 或 WAV 文件')
    }
    const durationMs = Math.round(decoded.duration * 1000)
    if (durationMs <= 0) {
      throw new SpeakerAudioError('音频文件没有可播放内容')
    }
    if (durationMs > SPEAKER_MAX_DURATION_MS) {
      throw new SpeakerAudioError('单次喊话音频不能超过 3 分钟')
    }
    const channels = Array.from({ length: decoded.numberOfChannels }, (_, index) => decoded.getChannelData(index))
    const mono = downmixSpeakerChannels(channels)
    const resampled = resampleSpeakerAudio(mono, decoded.sampleRate)
    const pcm = encodeSpeakerPcm16Le(resampled)
    if (pcm.byteLength > SPEAKER_MAX_PCM_BYTES) {
      throw new SpeakerAudioError('转换后的喊话音频不能超过 3 分钟')
    }
    return {
      blob: new Blob([pcm], { type: 'application/octet-stream' }),
      durationMs,
      fileName: pcmFileName(sourceName),
    }
  } finally {
    await context.close().catch(() => undefined)
  }
}
